<template>
  <yt-chart-card
    title="Importer un export Search Console"
    subtitle="La propriété YouTube d'une chaîne n'est pas exposée par l'API Search Console : déposez ici l'export du rapport Performances, filtre Découvrir."
    :sources="['gsc']"
    :loading="false"
  >
    <div class="yt-import">
      <rmr-notice variant="info">
        Export à récupérer dans
        <a href="https://search.google.com/search-console/discover" target="_blank" rel="noopener">Search Console → Performances → Découvrir</a>
        (propriété de la chaîne, type YouTube), bouton Exporter → Excel.
        Si l'export couvre plus d'un jour, les clics et impressions de chaque vidéo sont ventilés sur les jours de la période selon la courbe de la chaîne.
      </rmr-notice>

      <div class="yt-import__form">
        <rmr-file-input
          v-model="files"
          multiple
          accept=".xlsx"
          label="Exports Search Console (.xlsx)"
          :disabled="importing"
          class="yt-import__file"
          @change="results = []"
        />
        <rmr-select
          v-model="channelId"
          :items="channels"
          item-text="title"
          item-value="channel_id"
          label="Chaîne (si absente du nom de fichier)"
          clearable
          :disabled="importing || fileList.length > 1"
          class="yt-import__channel"
        />
        <rmr-action-button
          :label="importing ? 'Import…' : 'Importer'"
          :icon="icons.mdiCloudUploadOutline"
          variant="primary"
          :loading="importing"
          :disabled="importing || fileList.length === 0"
          @click="submit"
        />
      </div>

      <ul v-if="results.length" class="yt-import__results">
        <li v-for="(result, index) in results" :key="index" :class="['yt-import__result', `yt-import__result--${result.status}`]">
          <v-icon size="18" :color="result.status === 'ok' ? colors.green : colors.danger">
            {{ result.status === 'ok' ? icons.mdiCheckCircleOutline : icons.mdiAlertCircleOutline }}
          </v-icon>
          <span class="yt-import__filename">{{ result.filename }}</span>
          <span v-if="result.status === 'ok'" class="yt-import__detail">
            {{ result.channel_title }} · du {{ fDayLong(result.date_from) }} au {{ fDayLong(result.date_to) }} ·
            {{ fInt(result.videos) }} vidéo(s) · {{ fInt(result.clicks) }} clics
            <template v-if="result.listed_clicks !== result.clicks">({{ fInt(result.listed_clicks) }} sur les vidéos listées)</template>
            <template v-if="result.unknown_videos"> · {{ fInt(result.unknown_videos) }} vidéo(s) inconnue(s) du dashboard</template>
            <b v-if="result.beyond_last_date" class="yt-import__later"> · visible quand YouTube Analytics aura rattrapé cette date (dernière date disponible : {{ fDayLong(result.last_date) }})</b>
          </span>
          <span v-else class="yt-import__detail">{{ result.message }}</span>
        </li>
      </ul>

      <div class="yt-import__history">
        <div class="yt-import__history-title">Exports déposés</div>
        <p v-if="!loadingHistory && history.length === 0" class="yt-import__empty">Aucun export déposé pour le moment.</p>
        <div v-else class="yt-table-wrap">
          <table class="yt-table">
            <thead>
              <tr>
                <th>Chaîne</th>
                <th>Période</th>
                <th class="yt-table__num">Vidéos</th>
                <th class="yt-table__num">Clics</th>
                <th class="yt-table__num">Impressions</th>
                <th>Déposé par</th>
                <th>Le</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in history" :key="item.id">
                <td>{{ item.channel_title }}</td>
                <td>du {{ fDayLong(item.date_from) }} au {{ fDayLong(item.date_to) }}</td>
                <td class="yt-table__num">{{ fInt(item.videos) }}</td>
                <td class="yt-table__num">{{ fInt(item.clicks) }}</td>
                <td class="yt-table__num">{{ fInt(item.impressions) }}</td>
                <td>{{ item.uploaded_by || '—' }}</td>
                <td>{{ item.created_at ? fDateTime(item.created_at) : '—' }}</td>
                <td class="yt-table__num">
                  <rmr-icon-button :icon="icons.mdiDeleteOutline" variant="delete" size="xs" tooltip="Retirer cet export et ses données" :disabled="deleting" @click="askDelete(item)" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <rmr-confirm-dialog
      v-model="confirmOpen"
      title="Retirer cet export ?"
      :message="confirmMessage"
      confirm-label="Retirer"
      @confirm="confirmDelete"
    />
  </yt-chart-card>
</template>

<script>
import { mdiAlertCircleOutline, mdiCheckCircleOutline, mdiCloudUploadOutline, mdiDeleteOutline } from '@mdi/js'
import { computed, ref, toRefs, watch } from '@vue/composition-api'
import RmrActionButton from '@/components/ui/actions/RmrActionButton.vue'
import RmrConfirmDialog from '@/components/ui/actions/RmrConfirmDialog.vue'
import RmrIconButton from '@/components/ui/actions/RmrIconButton.vue'
import RmrNotice from '@/components/ui/feedback/RmrNotice.vue'
import RmrFileInput from '@/components/ui/forms/RmrFileInput.vue'
import RmrSelect from '@/components/ui/forms/RmrSelect.vue'
import useAxios from '@/hooks/useAxios'
import useProxy from '@/hooks/useProxy'
import { RMR_DANGER, RMR_GREEN } from '@/styles/tokens'
import YtChartCard from './YtChartCard.vue'
import { API_BASE } from './useYoutubePerf'
import { fDateTime, fDayLong, fInt } from './ytFormat'

const IMPORTS_URL = `${API_BASE}/discover/imports`

export default {
  name: 'YtDiscoverImportCard',
  components: { RmrActionButton, RmrConfirmDialog, RmrIconButton, RmrNotice, RmrFileInput, RmrSelect, YtChartCard },
  props: { active: { type: Boolean, default: false } },
  setup(props) {
    const { active } = toRefs(props)
    const { store } = useProxy()
    const { axiosGet, axiosPost, axiosDelete } = useAxios()

    const files = ref(null)
    const channelId = ref(null)
    const channels = ref([])
    const history = ref([])
    const results = ref([])
    const importing = ref(false)
    const loadingHistory = ref(false)
    const deleting = ref(false)
    const confirmOpen = ref(false)
    const toDelete = ref(null)

    const fileList = computed(() => {
      const value = files.value
      if (!value) return []
      return Array.isArray(value) ? value : [value]
    })

    const loadHistory = async () => {
      loadingHistory.value = true
      try {
        const { data } = await axiosGet(IMPORTS_URL)
        history.value = data.items || []
        channels.value = data.channels || []
      } finally {
        loadingHistory.value = false
      }
    }
    watch(active, (value) => { if (value && history.value.length === 0) loadHistory() }, { immediate: true })

    const submit = async () => {
      if (fileList.value.length === 0) return
      importing.value = true
      try {
        const formData = new FormData()
        fileList.value.forEach((file) => formData.append('files', file, file.name))
        if (channelId.value && fileList.value.length === 1) formData.append('channel_id', channelId.value)
        const { data } = await axiosPost(IMPORTS_URL, formData)
        results.value = data.results || []
        if (results.value.some((result) => result.status === 'ok')) {
          files.value = null
          channelId.value = null
          await loadHistory()
          // Toutes les vues du dashboard relisent la table Discover.
          store.commit('youtubePerf/bumpFiltersVersion')
        }
      } catch (error) {
        const message = error?.response?.data?.detail || 'Import impossible'
        results.value = fileList.value.map((file) => ({ filename: file.name, status: 'error', message }))
      } finally {
        importing.value = false
      }
    }

    const confirmMessage = computed(() => {
      const item = toDelete.value
      if (!item) return ''
      return `${item.channel_title}, du ${fDayLong(item.date_from)} au ${fDayLong(item.date_to)} : les données Discover importées de cette période seront retirées du dashboard.`
    })
    const askDelete = (item) => {
      toDelete.value = item
      confirmOpen.value = true
    }
    const confirmDelete = async () => {
      if (!toDelete.value) return
      deleting.value = true
      try {
        await axiosDelete(`${IMPORTS_URL}/${toDelete.value.id}`)
        await loadHistory()
        store.commit('youtubePerf/bumpFiltersVersion')
      } finally {
        deleting.value = false
        toDelete.value = null
      }
    }

    return {
      files, fileList, channelId, channels, history, results, importing, loadingHistory, deleting,
      confirmOpen, confirmMessage, submit, askDelete, confirmDelete,
      icons: { mdiAlertCircleOutline, mdiCheckCircleOutline, mdiCloudUploadOutline, mdiDeleteOutline },
      colors: { green: RMR_GREEN, danger: RMR_DANGER },
      fInt, fDayLong, fDateTime,
    }
  },
}
</script>

<style lang="scss" scoped>
.yt-import { display: flex; flex-direction: column; gap: $rmr-space-md; }
.yt-import__form {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) auto;
  gap: $rmr-space-sm;
  align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
}
.yt-import__results { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.yt-import__result {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: $rmr-radius-lg; background: $rmr-surface-2;
  font-size: $rmr-fs-sm; color: $rmr-text-secondary;
  &--ok { background: rgba(16, 185, 129, 0.08); }
  &--error { background: rgba(239, 68, 68, 0.08); }
}
.yt-import__filename { font-weight: 600; color: $rmr-text-primary; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 360px; }
.yt-import__detail { flex: 1; min-width: 0; }
.yt-import__later { color: $rmr-orange; font-weight: 600; }
.yt-import__history-title { font-size: $rmr-fs-sm; font-weight: 700; color: $rmr-text-primary; margin-bottom: 6px; }
.yt-import__empty { font-size: $rmr-fs-sm; color: $rmr-text-subtle; margin: 0; }
</style>
